import {useAuthStateManager} from '../../hooks/useAuthStateManager.tsx';
import React, {useEffect, useState} from 'react';
import useGetFiles from '../../api/uploads/useGetFiles.ts';
import {useQuery} from 'react-query';
import {
    CollectionModel,
    DirectoryModel,
    FileDataModel,
    UserCollectionType
} from '../../api/types.ts';
import useGetUserCollections from '../../api/collections/useGetUserCollections.ts';
import {ErrorState, LoadingState} from '../ds/StateView.tsx';
import RootDirectoryNames from './RootDirectoryNames.ts';
import filterDirectory from './filterDirectory.ts';
import SelectCollection from './SelectCollection.tsx';
import AssetSearch from './AssetSearch.tsx';
import SpinnerIfLoading from '../shared/SpinnerIfLoading.tsx';
import ContainerDirectory from './ContainerDirectory.tsx';
import humanFileSize from '../../lib/humanFileSize.ts';
import Containers from '../../lib/Containers.ts';
import RoleNames from '../../lib/RoleNames.ts';
import AssetPermissions from './AssetPermissions.ts';
import FileUsagesPanel, {fileUsagesPanelId} from './FileUsagesPanel.tsx';
import {useSidePanel} from '../ds/sidePanelContext.ts';

interface AssetsProps {
    containerName: string;
    showSelectCollection: boolean;
    folderPaths?: Record<string, string | null>;
}

const Assets: React.FC<AssetsProps> = ({
    containerName,
    showSelectCollection = true,
    folderPaths
}) => {
    const [canContribute, setCanContribute] = useState(false);
    const [canUpload, setCanUpload] = useState(false);

    const [selectedContainerName, setSelectedContainerName] =
        useState(containerName);

    const [editingPath, setEditingPath] = useState<string | null>(null);

    const {loggedInUserId, authData, isInRole} = useAuthStateManager();

    const uploadLimit = authData.upload_limit;

    const folderPath =
        folderPaths && selectedContainerName in folderPaths
            ? folderPaths[selectedContainerName]
            : null;

    const getPermissions: (
        directory: DirectoryModel,
        parentDirectory?: DirectoryModel | null
    ) => AssetPermissions = (directory, parentDirectory) => {
        const permissions: AssetPermissions = {
            canAddDirectory: canContribute,
            canRenameOrDeleteDirectory: canContribute,
            canRenameOrDeleteFile: canContribute,
            canUpload: canUpload
        };

        if (directory.isRoot && folderPath != null) {
            // A project's Assets tab. Its folder only exists once it holds a
            // file, so the top level uploads straight into it.
            permissions.canAddDirectory = false;
            permissions.canRenameOrDeleteDirectory = false;
            permissions.canRenameOrDeleteFile = false;
            permissions.uploadFolderPath = folderPath;
        } else if (directory.name == 'project_files') {
            permissions.canAddDirectory = false;
            permissions.canRenameOrDeleteDirectory = false;
            permissions.canRenameOrDeleteFile = false;
            permissions.canUpload = false;
        } else if (parentDirectory?.name === 'project_files') {
            permissions.canRenameOrDeleteDirectory = false;
            permissions.canUpload = false;
        }

        return permissions;
    };

    const fetchFiles = useGetFiles({
        containerName: selectedContainerName,
        folderPath
    });

    const {
        data: fetchedRootDirectory,
        isLoading,
        isError,
        refetch
    } = useQuery(['files', selectedContainerName, folderPath], fetchFiles);

    const [rootDirectory, setRootDirectory] = useState<DirectoryModel | null>(
        null
    );

    useEffect(() => {
        if (fetchedRootDirectory) {
            setRootDirectory({...fetchedRootDirectory});
        }
    }, [fetchedRootDirectory]);

    const [searchText, setSearchText] = useState('');
    const [onlyUnused, setOnlyUnused] = useState(false);

    const [usageFile, setUsageFile] = useState<FileDataModel | null>(null);
    const usagePanel = useSidePanel(fileUsagesPanelId);

    const showUsages = (file: FileDataModel) => {
        setUsageFile(file);
        usagePanel.open();
    };

    const [userCollections, setUserCollections] = useState<CollectionModel[]>(
        []
    );

    const fetchUserCollections = useGetUserCollections();

    const {data: userCollectionsData} = useQuery(
        ['userCollections', loggedInUserId],
        fetchUserCollections,
        {
            enabled: containerName != Containers.Binaries
        }
    );

    useEffect(() => {
        if (userCollectionsData) {
            setUserCollections(userCollectionsData);
        }
    }, [userCollectionsData]);

    useEffect(() => {
        if (containerName == Containers.Binaries) {
            const isAdmin = isInRole(RoleNames.Admin);
            setCanContribute(isAdmin);
            setCanUpload(isAdmin);
        } else if (userCollections && rootDirectory) {
            const selectedCollection = userCollections.find(
                c => c.containerName === containerName
            );

            if (selectedCollection) {
                const _canContribute =
                    selectedCollection.loggedInUserUserCollectionType !==
                    UserCollectionType.Viewer;

                setCanContribute(_canContribute);

                const _canUpload =
                    rootDirectory.name === RootDirectoryNames.Root &&
                    _canContribute &&
                    (uploadLimit === null ||
                        uploadLimit === 0 ||
                        uploadLimit === undefined ||
                        rootDirectory.length < uploadLimit);

                setCanUpload(_canUpload);
            } else {
                setCanContribute(false);
                setCanUpload(false);
            }
        }
    }, [containerName, userCollections, rootDirectory]);

    const onCollectionSelected = (containerName: string) =>
        setSelectedContainerName(containerName);

    if (isLoading) {
        return <LoadingState variant="rows" />;
    }

    if (isError || !rootDirectory) {
        return (
            <ErrorState
                title="Your assets didn't load"
                onRetry={() => refetch()}
            >
                Check your connection and try again. Nothing has been lost.
            </ErrorState>
        );
    }

    if (rootDirectory.name == RootDirectoryNames.StorageError) {
        return (
            <ErrorState
                title="Storage isn't reachable"
                onRetry={() => refetch()}
            >
                The files for {containerName} couldn't be opened. Try again in a
                moment; if it keeps happening, let us know.
            </ErrorState>
        );
    }

    // Until usage is fully worked out (just after a deploy), a count of 0 may be wrong: no "Not used", no filter.
    const referencesComplete = rootDirectory.referencesComplete === true;

    const filteredDirectory = filterDirectory(
        rootDirectory,
        ['*'],
        searchText,
        onlyUnused && referencesComplete
    ) ?? {
        ...rootDirectory,
        files: [],
        directories: []
    };

    return (
        <div>
            <div className="d-flex position-relative mb-2">
                {showSelectCollection && (
                    <SelectCollection
                        collections={userCollections}
                        containerName={selectedContainerName}
                        onCollectionSelected={onCollectionSelected}
                    />
                )}
                <AssetSearch
                    searchText={searchText}
                    setSearchText={setSearchText}
                />
                {referencesComplete && (
                    <div className="form-check form-switch ms-3 mb-0 align-self-center text-nowrap">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="AssetsOnlyUnused"
                            checked={onlyUnused}
                            onChange={event =>
                                setOnlyUnused(event.target.checked)
                            }
                        />
                        <label
                            className="form-check-label"
                            htmlFor="AssetsOnlyUnused"
                            title="Files nothing in saved content uses"
                        >
                            Unused
                        </label>
                    </div>
                )}
            </div>

            <FileUsagesPanel file={usageFile} />

            <SpinnerIfLoading loading={rootDirectory === null}>
                {rootDirectory.name === RootDirectoryNames.StorageError ? (
                    <p className="text-danger">
                        Storage error - container name: {selectedContainerName}
                    </p>
                ) : (
                    <div className="uploads">
                        <ContainerDirectory
                            directory={filteredDirectory}
                            setDirectory={dir => {
                                setRootDirectory({...rootDirectory, ...dir});
                            }}
                            containerName={selectedContainerName}
                            onFilesUploaded={() => refetch()}
                            getPermissions={getPermissions}
                            refetch={refetch}
                            editingPath={editingPath}
                            setEditingPath={setEditingPath}
                            referencesComplete={referencesComplete}
                            onShowUsages={showUsages}
                        />
                        <hr />
                        <div>
                            <small>
                                Total file size:{' '}
                                {humanFileSize(rootDirectory.length)}
                            </small>
                        </div>
                        <div>
                            <small>
                                Upload limit:{' '}
                                {humanFileSize(uploadLimit, 'Unlimited')}
                            </small>
                        </div>
                    </div>
                )}
            </SpinnerIfLoading>
        </div>
    );
};

export default Assets;
